import type { TemplateEditorTab } from './templateParts';

const INDENT = '  ';

export function formatCode(code: string, tab: TemplateEditorTab): string {
  if (!code.trim()) return code;

  switch (tab) {
    case 'css':
      return formatCss(code);
    case 'js':
      return formatJavaScript(code);
    case 'html':
    default:
      return formatHtml(code);
  }
}

function formatCss(code: string): string {
  const compact = code
    .replace(/\/\*[\s\S]*?\*\//g, (m) => m.replace(/\s+/g, ' '))
    .replace(/\s*{\s*/g, '{')
    .replace(/\s*}\s*/g, '}')
    .replace(/\s*;\s*/g, ';')
    .replace(/\s*:\s*/g, ': ')
    .trim();

  let out = '';
  let depth = 0;
  let chunk = '';

  for (const ch of compact) {
    chunk += ch;
    if (ch === '{') {
      out += `${INDENT.repeat(depth)}${chunk.trim()}\n`;
      chunk = '';
      depth += 1;
    } else if (ch === '}') {
      if (chunk.slice(0, -1).trim()) {
        out += `${INDENT.repeat(depth)}${chunk.slice(0, -1).trim()}\n`;
      }
      chunk = '';
      depth = Math.max(0, depth - 1);
      out += `${INDENT.repeat(depth)}}\n`;
    } else if (ch === ';') {
      out += `${INDENT.repeat(depth)}${chunk.trim()}\n`;
      chunk = '';
    }
  }
  if (chunk.trim()) out += `${INDENT.repeat(depth)}${chunk.trim()}\n`;

  return out.trimEnd() + '\n';
}

function formatJavaScript(code: string): string {
  const lines = code.replace(/\r\n/g, '\n').split('\n');
  let out = '';
  let depth = 0;

  for (let raw of lines) {
    const trimmed = raw.trim();
    if (!trimmed) {
      out += '\n';
      continue;
    }

    if (/^[}\])]/.test(trimmed)) {
      depth = Math.max(0, depth - (trimmed.match(/^[}\])]+/)?.[0].length ?? 1));
    }

    out += `${INDENT.repeat(depth)}${trimmed}\n`;

    const openCount = (trimmed.match(/[{[(]/g) ?? []).length;
    const closeCount = (trimmed.match(/[}\])]/g) ?? []).length;
    depth = Math.max(0, depth + openCount - closeCount);
  }

  return out.trimEnd() + '\n';
}

function formatHtml(code: string): string {
  const voidTags = new Set([
    'area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input', 'link',
    'meta', 'param', 'source', 'track', 'wbr',
  ]);

  const normalized = code
    .replace(/\r\n/g, '\n')
    .replace(/<!--[\s\S]*?-->/g, (m) => `\n${m}\n`)
    .replace(/>\s*</g, '>\n<')
    .replace(/^\s+|\s+$/g, '');

  const lines = normalized.split('\n').map((l) => l.trim()).filter(Boolean);
  let out = '';
  let depth = 0;

  for (const line of lines) {
    if (line.startsWith('</')) {
      depth = Math.max(0, depth - 1);
      out += `${INDENT.repeat(depth)}${line}\n`;
      continue;
    }

    if (line.startsWith('<!') || line.startsWith('<?') || line.startsWith('<!--')) {
      out += `${INDENT.repeat(depth)}${line}\n`;
      continue;
    }

    const selfClosing = line.endsWith('/>');
    const tagMatch = line.match(/^<([a-zA-Z0-9-]+)/);
    const tag = tagMatch?.[1]?.toLowerCase() ?? '';
    const hasCloseInline = line.includes('</');

    out += `${INDENT.repeat(depth)}${line}\n`;

    if (tag && !selfClosing && !voidTags.has(tag) && !hasCloseInline) {
      depth += 1;
    }
  }

  return out.trimEnd() + '\n';
}
